import type {
  StorageAccessPolicy,
  StorageOperationError,
  StoragePolicyResourceRef,
  StoragePolicyGrant,
  StoragePolicyGrantResolution,
  StorageSasPolicyRequest,
  StorageSasRequest,
} from '../../types/azureStorage';
import { STORAGE_READ_ONLY_REASON } from './azureStorageRefusal';
import { DIRECTORY_POLICY_REASON, hnsSasPathProblem, hnsSasResourceKind } from './azureStorageHnsSas';

export type StorageSasResourceKind = 'account' | 'container' | 'blob' | 'table' | 'queue'
  // S7
  | 'share' | 'file'
  // S8
  | 'directory';
type PermissionResult = { ok: true; value: string } | { ok: false; problem: StorageOperationError };

/** The SDK's canonical letter order per resource (`*SASPermissions.toString()`); requests are normalized to it. */
export const SAS_PERMISSION_ORDER: Record<StorageSasResourceKind, string> = {
  account: 'rwdxftlacupiy',
  container: 'racwdxltmeiyf',
  blob: 'racwdxtmeiy',
  table: 'raud',
  queue: 'raup',
  share: 'rcwdl',   // S7: file SDK ShareSASPermissions order
  file: 'rcwd',     // S7: FileSASPermissions order
  directory: 'racwdlmeop', // S8: DirectorySASPermissions.toString() (NOTES S8 row S1)
};

const problem = (code: string, message: string): StorageOperationError => ({
  category: code === 'ReadOnlyConnection' ? 'authorization' : 'invalid_input',
  code,
  retryable: false,
  message,
});

export function normalizeSasPermissions(kind: StorageSasResourceKind, letters: string): PermissionResult {
  const allowed = SAS_PERMISSION_ORDER[kind];
  for (const letter of letters) {
    if (!allowed.includes(letter)) {
      return { ok: false, problem: problem('InvalidSasPermission', `The ${kind} SAS permissions contain a letter that is not valid for that resource.`) };
    }
  }
  const present = new Set(letters);
  return { ok: true, value: [...allowed].filter((letter) => present.has(letter)).join('') };
}

export const nonMutatingSasLetters = (kind: StorageSasResourceKind): string => (kind === 'account' || kind === 'container' || kind === 'share' || kind === 'directory' ? 'rl' : 'r');

/**
 * The resource whose stored access policies a SAS target signs under: a blob uses its container's, a file its share's
 * (the live share — a snapshot has no policies of its own), a container, table, queue or share its own; the account
 * and a folder have none (follow-up 2026-09-25: tables and queues joined; S7 Task 7.17: shares and files).
 */
export function policyResourceOf(resource: StorageSasRequest['resource']): StoragePolicyResourceRef | undefined {
  if (resource.kind === 'container' || resource.kind === 'blob') return { kind: 'container', container: resource.container };
  if (resource.kind === 'table') return { kind: 'table', table: resource.table };
  if (resource.kind === 'queue') return { kind: 'queue', queue: resource.queue };
  if (resource.kind === 'share' || resource.kind === 'file') return { kind: 'share', share: resource.share };
  // S8: the root and a file sign under their file system's (container) policies; a directory has none.
  if (resource.kind === 'hnsPath' && hnsSasResourceKind(resource) !== 'directory') return { kind: 'container', container: resource.fileSystem };
  return undefined;
}

/** The letter alphabet a SAS target signs with; an `hnsPath` as its directory / container / blob (S8, D7). */
export function sasResourceKindOf(resource: StorageSasRequest['resource']): StorageSasResourceKind | undefined {
  const kind = resource.kind;
  if (kind === 'hnsPath') return hnsSasResourceKind(resource);
  return kind === 'account' || kind === 'container' || kind === 'blob' || kind === 'table' || kind === 'queue' || kind === 'share' || kind === 'file'
    ? kind
    : undefined;
}

/**
 * S3 lists neither snapshots nor versions (S6 owns them), and the signers target the
 * current blob, so a historical ref is refused rather than silently widened to the base
 * blob (review B M1). S6 lifts this when it signs `sr=bs` / `sr=bv` and emits the
 * `snapshot` / `versionid` URL parameter.
 */
export function historicalBlobSasProblem(resource: StorageSasRequest['resource']): StorageOperationError | undefined {
  if ((resource.kind === 'share' || resource.kind === 'file') && resource.snapshot !== undefined) {
    return problem('HistoricalShareSasUnsupported', 'A SAS for a share snapshot is not available; generate it for the live share.');   // S7
  }
  return resource.kind === 'blob' && (resource.snapshot !== undefined || resource.versionId !== undefined)
    ? problem('HistoricalBlobSasUnsupported', 'A SAS for a blob snapshot or version is not available; generate it for the current blob.')
    : undefined;
}

function dateValue(value: string | undefined): number | undefined {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
}

function ipv4Value(value: string): number | undefined {
  const parts = value.split('.');
  if (parts.length !== 4) return undefined;
  let result = 0;
  for (const part of parts) {
    if (!/^\d{1,3}$/u.test(part)) return undefined;
    const octet = Number(part);
    if (octet > 255) return undefined;
    result = (result * 256) + octet;
  }
  return result;
}

function ipRangeProblem(request: StorageSasRequest): StorageOperationError | undefined {
  if (!request.ipRange) return undefined;
  const start = ipv4Value(request.ipRange.start);
  const end = request.ipRange.end ? ipv4Value(request.ipRange.end) : start;
  return start === undefined || end === undefined || start > end
    ? problem('InvalidSasIpRange', 'The SAS IP range must contain valid ascending IPv4 addresses.')
    : undefined;
}

function requestDates(request: StorageSasRequest): { startsOn?: string; expiresOn?: string } {
  return request.mode === 'ad-hoc' ? request : request.supplement ?? {};
}

const POLICY_RESOURCE_PROBLEM = (): StorageOperationError =>
  problem('PolicyModeUnsupportedResource', 'Stored access policies belong to a blob container, table, queue or file share; the account has none. Generate an ad-hoc SAS instead.');

export function sasRequestProblem(
  request: StorageSasRequest,
  options: { readOnly?: boolean; now?: Date } = {},
): StorageOperationError | undefined {
  const resourceKind = sasResourceKindOf(request.resource);
  if (!resourceKind) return problem('UnsupportedSasResource', 'A SAS can be generated only for an account, container, blob, table, queue, share or file.');
  const historical = request.resource.kind === 'hnsPath' ? hnsSasPathProblem(request.resource) : historicalBlobSasProblem(request.resource);
  if (historical) return historical;
  if (request.mode === 'ad-hoc' && !request.permissions) {
    return problem('SasPermissionsRequired', 'An ad-hoc SAS must include at least one permission.');
  }
  if (request.mode === 'ad-hoc' && !request.expiresOn) {
    return problem('SasExpiryRequired', 'An ad-hoc SAS must include a valid expiry.');
  }
  if (request.mode === 'policy' && (request as { kind: unknown }).kind !== 'service') {
    return problem('PolicyModeRequiresServiceSas', 'A stored access policy can be used only with a service SAS.');
  }
  if (request.mode === 'policy' && !policyResourceOf(request.resource)) return resourceKind === 'directory' ? problem('PolicyModeUnsupportedResource', DIRECTORY_POLICY_REASON) : POLICY_RESOURCE_PROBLEM();
  if (request.mode === 'policy' && !request.identifier) return problem('PolicyIdentifierRequired', 'Choose a stored access policy to generate a SAS under it.');
  if (request.mode === 'ad-hoc' && request.kind === 'account') {
    if (!request.services?.length) return problem('AccountSasServicesRequired', 'An account SAS must include at least one storage service.');
    if (!request.resourceTypes?.length) return problem('AccountSasResourceTypesRequired', 'An account SAS must include at least one resource type.');
  }
  const ipProblem = ipRangeProblem(request);
  if (ipProblem) return ipProblem;
  const dateProblem = datesProblem(requestDates(request), request.kind === 'user-delegation', options.now);
  if (dateProblem) return dateProblem;
  const letters = request.mode === 'ad-hoc' ? request.permissions : request.supplement?.permissions;
  return letters === undefined ? undefined : lettersProblem(resourceKind, letters, options.readOnly);
}

function datesProblem(dates: { startsOn?: string; expiresOn?: string }, userDelegation: boolean, at: Date | undefined): StorageOperationError | undefined {
  const expiry = dateValue(dates.expiresOn);
  const starts = dateValue(dates.startsOn);
  if (dates.expiresOn && expiry === undefined) return problem('InvalidSasExpiry', 'The SAS expiry must be a valid date and time.');
  if (dates.startsOn && starts === undefined) return problem('InvalidSasStart', 'The SAS start must be a valid date and time.');
  const now = (at ?? new Date()).getTime();
  if (expiry !== undefined && expiry <= now) return problem('SasExpiryInPast', 'The SAS expiry must be in the future.');
  if (starts !== undefined && expiry !== undefined && starts >= expiry) return problem('SasStartAfterExpiry', 'The SAS start must be earlier than its expiry.');
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  if (userDelegation && expiry !== undefined && expiry - (starts ?? now) > sevenDays) {
    return problem('UserDelegationWindowTooLong', 'A user-delegation SAS cannot have a validity window longer than seven days.');
  }
  // The delegation key's start and expiry must both lie within seven days of NOW (Get User Delegation Key);
  // with start < expiry and expiry > now, bounding the expiry bounds both (review B minor).
  if (userDelegation && expiry !== undefined && expiry - now > sevenDays) {
    return problem('UserDelegationExpiryTooFar', 'A user-delegation SAS must expire within seven days from now.');
  }
  return undefined;
}

function lettersProblem(kind: StorageSasResourceKind, letters: string, readOnly: boolean | undefined): StorageOperationError | undefined {
  const normalized = normalizeSasPermissions(kind, letters);
  if (!normalized.ok) return normalized.problem;
  if (readOnly && [...normalized.value].some((letter) => !nonMutatingSasLetters(kind).includes(letter))) {
    return problem('ReadOnlyConnection', STORAGE_READ_ONLY_REASON);
  }
  return undefined;
}

/**
 * Re-checks a RESOLVED policy grant right before signing (Q3). Letters the policy itself
 * carries are letters of the policy-bearing resource (`policyResourceOf`) — a container ACL
 * identifier is stored with the container alphabet and Q3's container set `rl` even when the
 * token targets one blob (`sr=b&si=…` emits no `sp`, so the letters are never token letters;
 * review B M4), a share identifier with the share alphabet `rcwdl` and the set `rl` even when
 * the token targets one file (S7); a table or queue identifier with its own alphabet (`raud` /
 * `raup`) and the read-only set `r`. Only a supplemented `sp` is a token field and is checked
 * against the signed resource's alphabet.
 */
export function policyGrantProblem(
  request: StorageSasPolicyRequest,
  grant: StoragePolicyGrant,
  options: { readOnly?: boolean; now?: Date } = {},
): StorageOperationError | undefined {
  const resourceKind = sasResourceKindOf(request.resource);
  const owner = policyResourceOf(request.resource);
  if (!resourceKind || !owner) return POLICY_RESOURCE_PROBLEM();
  const dateProblem = datesProblem(grant, false, options.now);
  if (dateProblem) return dateProblem;
  const letterKind = grant.supplemented.includes('permissions') ? resourceKind : owner.kind;
  return lettersProblem(letterKind, grant.permissions, options.readOnly);
}

export function resolvePolicyGrant(
  policy: StorageAccessPolicy,
  supplement: { permissions?: string; startsOn?: string; expiresOn?: string } = {},
): StoragePolicyGrantResolution {
  // An empty field is an absent one (review B M3): the SDK deserializes `<Permission/>` as ''.
  const present = (value: string | undefined): string | undefined => (value ? value : undefined);
  const permissions = present(policy.permissions) ?? present(supplement.permissions);
  if (!permissions) {
    return { ok: false, problem: problem('PolicyIncomplete', 'This stored access policy sets no permissions; add them to the policy, or set them on the token.') };
  }
  const expiresOn = present(policy.expiresOn) ?? present(supplement.expiresOn);
  if (!expiresOn) {
    return { ok: false, problem: problem('PolicyIncomplete', 'This stored access policy sets no expiry; add one to the policy, or set it on the token.') };
  }
  const startsOn = present(policy.startsOn) ?? present(supplement.startsOn);
  const supplemented: Array<'permissions' | 'startsOn' | 'expiresOn'> = [];
  if (!policy.permissions && supplement.permissions) supplemented.push('permissions');
  if (!policy.startsOn && supplement.startsOn) supplemented.push('startsOn');
  if (!policy.expiresOn && supplement.expiresOn) supplemented.push('expiresOn');
  return { ok: true, value: { permissions, startsOn, expiresOn, supplemented } };
}
